// ============================================================================
// NOTES & OBSERVATIONS - AUDITOR PAGE
// ============================================================================
// Purpose: Record audit notes and observations during reviews
// ============================================================================

import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../../contexts/AuthContext';
import { auditApi } from '../../audit/api';
import { complianceExecutionApi } from '../../compliance/execution/api/complianceExecution.api';
import { RefreshCw, Plus, X, Edit3 } from 'lucide-react';
import PageInfoButton from '@/components/PageInfoButton';

const NotesObservations: React.FC = () => {
  const { user } = useAuth();
  const [notes, setNotes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddNote, setShowAddNote] = useState(false);
  const [editingNote, setEditingNote] = useState<any | null>(null);
  const [frameworks, setFrameworks] = useState<any[]>([]);
  const [compliancePages, setCompliancePages] = useState<any[]>([]);
  const [newNote, setNewNote] = useState<{
    compliance_page_id: number;
    company_id: number;
    framework_id: number;
    requirement_id: number;
    finding_type: 'non_conformity' | 'observation' | 'opportunity_for_improvement' | 'positive_practice';
    severity: 'critical' | 'major' | 'minor';
    title: string;
    description: string;
  }>({
    compliance_page_id: 0,
    company_id: user?.company_id || 0,
    framework_id: 0,
    requirement_id: 0,
    finding_type: 'observation',
    severity: 'minor',
    title: '',
    description: ''
  });

  useEffect(() => {
    loadNotes();
    loadFrameworks();
  }, []);

  const loadNotes = async () => {
    try {
      const auditFindings = await auditApi.getAuditFindings();
      setNotes(auditFindings);
    } catch (error) {
      console.error('Failed to load notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFrameworks = async () => {
    try {
      const companyFrameworks = await complianceExecutionApi.getCompanyFrameworks();
      setFrameworks(companyFrameworks);
    } catch (error) {
      console.error('Failed to load frameworks:', error);
    }
  };

  const loadCompliancePages = async (frameworkId: number) => {
    try {
      const pages = await complianceExecutionApi.getCompanyCompliancePages();
      const filteredPages = pages.filter((page: any) => page.framework_id === frameworkId);
      setCompliancePages(filteredPages);
    } catch (error) {
      console.error('Failed to load compliance pages:', error);
    }
  };

  const handleEditNote = (note: any) => {
    setEditingNote(note);
  };

  const handleResolveNote = async (noteId: number) => {
    try {
      const noteToResolve = notes.find(note => note.id === noteId);
      if (noteToResolve) {
        await auditApi.updateAuditFinding(noteId, { ...noteToResolve, status: 'closed' });
        setNotes(prev => prev.map(note =>
          note.id === noteId ? { ...note, status: 'closed' } : note
        ));
        toast.success('Finding resolved successfully!');
      }
    } catch (error) {
      console.error('Failed to resolve note:', error);
      toast.error('Failed to resolve finding. Please try again.');
    }
  };

  const handleReopenNote = async (noteId: number) => {
    try {
      const noteToReopen = notes.find(note => note.id === noteId);
      if (noteToReopen) {
        await auditApi.updateAuditFinding(noteId, { ...noteToReopen, status: 'open' });
        setNotes(prev => prev.map(note =>
          note.id === noteId ? { ...note, status: 'open' } : note
        ));
        toast.success('Finding reopened successfully!');
      }
    } catch (error) {
      console.error('Failed to reopen note:', error);
      toast.error('Failed to reopen finding. Please try again.');
    }
  };

  const handleUpdateNote = async () => {
    if (!editingNote) return;

    try {
      await auditApi.updateAuditFinding(editingNote.id, editingNote);
      setNotes(prev => prev.map(note =>
        note.id === editingNote.id ? editingNote : note
      ));
      setEditingNote(null);
      toast.success('Note updated successfully!');
    } catch (error) {
      console.error('Failed to update note:', error);
      toast.error('Failed to update note. Please try again.');
    }
  };

  const getTypeColor = (findingType: string) => {
    switch (findingType) {
      case 'observation': return 'text-blue-700 bg-blue-100';
      case 'finding': return 'text-red-700 bg-red-100';
      case 'positive_practice': return 'text-green-700 bg-green-100';
      default: return 'text-gray-700 bg-gray-100';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'text-red-700 bg-red-100';
      case 'major': return 'text-orange-700 bg-orange-100';
      case 'minor': return 'text-yellow-700 bg-yellow-100';
      default: return 'text-gray-700 bg-gray-100';
    }
  };

  const getStatusDisplay = (status: string) => {
    switch (status) {
      case 'open': return 'Open';
      case 'closed': return 'Resolved';
      case 'in_progress': return 'In Progress';
      case 'verified': return 'Verified';
      default: return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'text-yellow-700 bg-yellow-100';
      case 'closed': return 'text-green-700 bg-green-100';
      case 'in_progress': return 'text-blue-700 bg-blue-100';
      case 'verified': return 'text-purple-700 bg-purple-100';
      default: return 'text-gray-700 bg-gray-100';
    }
  };

  const handleAddNote = async () => {
    try {
      await auditApi.createAuditFinding(newNote);
      await loadNotes();
      setNewNote({
        compliance_page_id: 0,
        company_id: 0,
        framework_id: 0,
        requirement_id: 0,
        finding_type: 'observation',
        severity: 'minor',
        title: '',
        description: ''
      });
      setShowAddNote(false);
    } catch (error) {
      console.error('Failed to create audit finding:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <RefreshCw className="text-blue-600 animate-spin" size={40} />
          <p className="text-slate-600 font-medium text-sm">Loading notes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">Notes & Observations</h1><PageInfoButton /></div>
            <p className="text-slate-500 text-sm">Record findings, observations, and insights from compliance reviews</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowAddNote(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white font-medium text-sm rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus size={16} />
              Add Note
            </button>
            <button
              onClick={loadNotes}
              className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <RefreshCw size={16} />
              Refresh
            </button>
          </div>
        </div>

        {/* Add Note Modal */}
        {showAddNote && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-3xl border border-slate-100 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-bold text-slate-900">Add New Note</h2>
                  <button
                    onClick={() => setShowAddNote(false)}
                    className="text-slate-400 hover:text-slate-600"
                  >
                    <X size={20} />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Company</label>
                      <input
                        type="text"
                        value={`Company ${newNote.company_id}`}
                        disabled
                        className="w-full p-2.5 border border-slate-200 rounded-lg bg-slate-50 text-slate-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Framework</label>
                      <select
                        value={newNote.framework_id}
                        onChange={(e) => {
                          const frameworkId = parseInt(e.target.value) || 0;
                          setNewNote({ ...newNote, framework_id: frameworkId, compliance_page_id: 0, requirement_id: 0 });
                          if (frameworkId) {
                            loadCompliancePages(frameworkId);
                          } else {
                            setCompliancePages([]);
                          }
                        }}
                        className="w-full p-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      >
                        <option value={0}>Select Framework</option>
                        {frameworks.map((framework) => (
                          <option key={framework.id} value={framework.id}>
                            {framework.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Compliance Page</label>
                      <select
                        value={newNote.compliance_page_id}
                        onChange={(e) => setNewNote({ ...newNote, compliance_page_id: parseInt(e.target.value) || 0, requirement_id: 0 })}
                        disabled={!newNote.framework_id}
                        className="w-full p-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50 disabled:text-slate-500"
                      >
                        <option value={0}>Select Compliance Page</option>
                        {compliancePages.map((page) => (
                          <option key={page.id} value={page.id}>
                            {page.title}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Requirement</label>
                      <select
                        value={newNote.requirement_id}
                        onChange={(e) => setNewNote({ ...newNote, requirement_id: parseInt(e.target.value) || 0 })}
                        disabled={!newNote.compliance_page_id}
                        className="w-full p-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50 disabled:text-slate-500"
                      >
                        <option value={0}>Select Requirement</option>
                        <option value={1}>Requirement 1</option>
                        <option value={2}>Requirement 2</option>
                        <option value={3}>Requirement 3</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Finding Type</label>
                      <select
                        value={newNote.finding_type}
                        onChange={(e) => setNewNote({ ...newNote, finding_type: e.target.value as typeof newNote.finding_type })}
                        className="w-full p-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="observation">Observation</option>
                        <option value="non_conformity">Non-Conformity</option>
                        <option value="positive_practice">Positive Practice</option>
                        <option value="opportunity_for_improvement">Opportunity for Improvement</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Severity</label>
                      <select
                        value={newNote.severity}
                        onChange={(e) => setNewNote({ ...newNote, severity: e.target.value as typeof newNote.severity })}
                        className="w-full p-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="minor">Minor</option>
                        <option value="major">Major</option>
                        <option value="critical">Critical</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
                    <input
                      type="text"
                      value={newNote.title}
                      onChange={(e) => setNewNote({ ...newNote, title: e.target.value })}
                      placeholder="Brief title for the note"
                      className="w-full p-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                    <textarea
                      value={newNote.description}
                      onChange={(e) => setNewNote({ ...newNote, description: e.target.value })}
                      placeholder="Detailed notes and observations..."
                      rows={6}
                      className="w-full p-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleAddNote}
                      className="px-4 py-2.5 bg-blue-600 text-white font-medium text-sm rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Save Note
                    </button>
                    <button
                      onClick={() => setShowAddNote(false)}
                      className="px-4 py-2.5 border border-slate-200 text-slate-700 font-medium text-sm rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Note Modal */}
        {editingNote && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-3xl border border-slate-100 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-bold text-slate-900">Edit Note</h2>
                  <button
                    onClick={() => setEditingNote(null)}
                    className="text-slate-400 hover:text-slate-600"
                  >
                    <X size={20} />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
                    <input
                      type="text"
                      value={editingNote.title}
                      onChange={(e) => setEditingNote({ ...editingNote, title: e.target.value })}
                      className="w-full p-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                    <textarea
                      value={editingNote.description}
                      onChange={(e) => setEditingNote({ ...editingNote, description: e.target.value })}
                      rows={6}
                      className="w-full p-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Finding Type</label>
                      <select
                        value={editingNote.finding_type}
                        onChange={(e) => setEditingNote({ ...editingNote, finding_type: e.target.value })}
                        className="w-full p-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="non_conformity">Non-Conformity</option>
                        <option value="observation">Observation</option>
                        <option value="opportunity_for_improvement">Opportunity for Improvement</option>
                        <option value="positive_practice">Positive Practice</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Severity</label>
                      <select
                        value={editingNote.severity}
                        onChange={(e) => setEditingNote({ ...editingNote, severity: e.target.value })}
                        className="w-full p-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="critical">Critical</option>
                        <option value="major">Major</option>
                        <option value="minor">Minor</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleUpdateNote}
                      className="px-4 py-2.5 bg-blue-600 text-white font-medium text-sm rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Update Note
                    </button>
                    <button
                      onClick={() => setEditingNote(null)}
                      className="px-4 py-2.5 border border-slate-200 text-slate-700 font-medium text-sm rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Notes List */}
        <div className="space-y-4 mb-6">
          {notes.map((note) => (
            <div key={note.id} className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-sm font-semibold text-slate-900">{note.title}</h3>
                  <span className={`px-2 py-1 rounded-md font-bold text-[10px] ${getTypeColor(note.finding_type)}`}>
                    {note.finding_type}
                  </span>
                  <span className={`px-2 py-1 rounded-md font-bold text-[10px] ${getSeverityColor(note.severity)}`}>
                    {note.severity}
                  </span>
                  <span className={`px-2 py-1 rounded-md font-bold text-[10px] ${getStatusColor(note.status)}`}>
                    {getStatusDisplay(note.status)}
                  </span>
                </div>
                <span className="text-xs text-slate-500 whitespace-nowrap ml-3">
                  {new Date(note.created_at).toLocaleDateString()}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                <div>
                  <p className="text-xs text-slate-500">Company ID</p>
                  <p className="text-sm font-medium text-slate-900">{note.company_id}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Framework ID</p>
                  <p className="text-sm font-medium text-slate-900">{note.framework_id}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Requirement ID</p>
                  <p className="text-sm font-medium text-slate-900">{note.requirement_id}</p>
                </div>
              </div>

              <p className="text-sm text-slate-600 mb-4">{note.description}</p>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleEditNote(note)}
                  className="px-4 py-2.5 border border-slate-200 text-slate-700 font-medium text-sm rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Edit
                </button>
                {note.status === 'closed' ? (
                  <button
                    onClick={() => handleReopenNote(note.id)}
                    className="px-4 py-2.5 bg-blue-600 text-white font-medium text-sm rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Reopen
                  </button>
                ) : (
                  <button
                    onClick={() => handleResolveNote(note.id)}
                    className="px-4 py-2.5 border border-slate-200 text-slate-700 font-medium text-sm rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    Resolve
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {notes.length === 0 && (
          <div className="bg-white rounded-3xl border border-slate-100 p-12">
            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center mx-auto mb-3">
              <Edit3 size={20} className="text-slate-400" />
            </div>
            <h3 className="text-sm font-semibold text-slate-900 mb-1">No Notes Yet</h3>
            <p className="text-sm text-slate-500">Start recording your audit observations and findings.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotesObservations;
